import React, { useState } from 'react';
import {
  Modal,
  Button,
  Input,
  Select,
  Typography,
  Row,
  Col,
  Space,
} from 'antd';
import { CopyOutlined } from '@ant-design/icons';
// Hooks
import { useGetCampaignCollabRoles } from 'hooks';
// Helpers
import getEnvVariables from 'api/env';
// Api
import {
  CREATE_CAMPAIGN_COLLAB_INVITE,
  SEND_CAMPAIGN_COLLAB_INVITE,
} from 'api/campaignV2/mutations';
// Types
import {
  CreateCampaignCollabInvite,
  CreateCampaignCollabInviteVariables,
} from 'api/campaignV2/types/CreateCampaignCollabInvite';
import { useMutation } from '@apollo/client';
import {
  SendCampaignCollabInvite,
  SendCampaignCollabInviteVariables,
} from 'api/campaignV2/types/SendCampaignCollabInvite';
// Ui
import { errorNotification, successNotification } from 'ui/Notification';
// Styles
import styles from './InviteCollaboratorModal.module.scss';

const { Title, Text } = Typography;

type CollaboratorInviteModalProps = {
  campaignId: string;
  visible: boolean;
  onClose: () => void;
};

const CollaboratorInviteModal = ({
  campaignId,
  visible,
  onClose,
}: CollaboratorInviteModalProps): JSX.Element => {
  const { data: campaignCollabRoles } = useGetCampaignCollabRoles();

  const [linkRole, setLinkRole] = useState('');
  const [emailRole, setEmailRole] = useState('');
  const [email, setEmail] = useState('');

  const [createCampaignCollabInvite] = useMutation<
    CreateCampaignCollabInvite,
    CreateCampaignCollabInviteVariables
  >(CREATE_CAMPAIGN_COLLAB_INVITE);

  const [sendCampaignCollabInvite] = useMutation<
    SendCampaignCollabInvite,
    SendCampaignCollabInviteVariables
  >(SEND_CAMPAIGN_COLLAB_INVITE);

  const permissionsOptions =
    campaignCollabRoles?.getPublicCampaignCollabRoles?.map((item) => ({
      label: item.roleId,
      value: item.roleId,
    }));

  const [inviteLink, setInviteLink] = useState('');

  const handleGetInviteLink = async () => {
    if (linkRole === '') {
      errorNotification('Please select Role');
      return;
    }
    try {
      const { data } = await createCampaignCollabInvite({
        variables: {
          input: {
            campaignId,
            roleId: linkRole,
          },
        },
      });

      successNotification('Invite created!');

      if (data) {
        const inviteLink = `${getEnvVariables.CAMPAIGN_APP_URL}/collaboration-invite/${campaignId}/${data?.createCampaignCollabInvite?.invite}`;
        setInviteLink(inviteLink);
      }
    } catch (error: any) {
      errorNotification(
        error?.message || 'Something went wrong, please try again later'
      );
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    successNotification('Link copied to clipboard');
  };

  const handleSendInvite = async () => {
    if (emailRole === '') {
      errorNotification('Please select role');
      return;
    }

    if (email === '') {
      errorNotification('Please add email');
      return;
    }

    try {
      await sendCampaignCollabInvite({
        variables: {
          input: {
            campaignId,
            roleId: emailRole,
            email,
          },
        },
      });

      successNotification('Invite sent!');

      onClose();
    } catch (error: any) {
      errorNotification(
        error?.message || 'Something went wrong, please try again later'
      );
    }
  };
  return (
    <Modal
      title={<Title level={3}>Add Collaborators</Title>}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={600}
      centered
    >
      <Space direction="vertical" align="start">
        <Input.Group compact>
          <div>
            <Text strong>Invite Link</Text>
            <Row gutter={16}>
              <Col flex="auto">
                <Input
                  style={{ width: 'calc(100% - 50px)' }}
                  placeholder="Invite Link"
                  value={inviteLink}
                  disabled={!inviteLink}
                />
                <Button type="primary" onClick={handleCopyLink}>
                  <CopyOutlined />
                </Button>
              </Col>
              <Col>
                <Select
                  placeholder="Select..."
                  className={styles.roleSelect}
                  options={permissionsOptions}
                  value={linkRole}
                  onChange={(value: string) => setLinkRole(value)}
                ></Select>
              </Col>
              <Col>
                <Button type="primary" onClick={handleGetInviteLink}>
                  GET LINK
                </Button>
              </Col>
            </Row>
          </div>
        </Input.Group>
        <div>
          <Text strong>Invite By Email</Text>
          <Row gutter={16}>
            <Col flex="auto">
              <Input
                placeholder="Invite by Email"
                onChange={(e) => setEmail(e.target.value)}
              />
            </Col>
            <Col>
              <Select
                placeholder="Select..."
                className={styles.roleSelect}
                options={permissionsOptions}
                value={emailRole}
                onChange={(value: string) => setEmailRole(value)}
              ></Select>
            </Col>
            <Col>
              <Button type="primary" onClick={handleSendInvite}>
                SEND INVITE
              </Button>
            </Col>
          </Row>
        </div>
      </Space>
    </Modal>
  );
};

export default CollaboratorInviteModal;
